import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Event as EventIcon,
  CalendarMonth as CalendarIcon,
  LocalActivity as PackageIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  DirectionsCar as VehicleIcon,
  LocalGasStation as FuelIcon,
  LocalGasStation as FuelExpenseIcon,
  Assessment as ReportsIcon,
  PictureAsPdf as PdfIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logoImage from '../logo_divertilandia.png';
import InstallPWA from './InstallPWA';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/divertilandia/dashboard' },
  { text: 'Paquetes', icon: <PackageIcon />, path: '/divertilandia/packages' },
  { text: 'Calendario', icon: <CalendarIcon />, path: '/divertilandia/calendar' },
  { text: 'Eventos', icon: <EventIcon />, path: '/divertilandia/events' },
  { text: 'Inventario', icon: <InventoryIcon />, path: '/divertilandia/inventory' },
  { text: 'Tipos de Juguetes', icon: <CategoryIcon />, path: '/divertilandia/game-types' },
  { text: 'Gastos', icon: <ReceiptIcon />, path: '/divertilandia/expenses' },
  { text: 'Vehículos', icon: <VehicleIcon />, path: '/divertilandia/vehicles' },
  { text: 'Precios de Combustible', icon: <FuelIcon />, path: '/divertilandia/fuel-prices' },
  { text: 'Gastos de Gasolina', icon: <FuelExpenseIcon />, path: '/divertilandia/fuel-expenses' },
  { text: 'Reportes', icon: <ReportsIcon />, path: '/divertilandia/reports' },
  { text: 'Contratos', icon: <PdfIcon />, path: '/divertilandia/contracts' },
  { text: 'Configuraciones', icon: <SettingsIcon />, path: '/divertilandia/settings' },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('¿Estás seguro de que quieres cerrar sesión?')) {
      logout();
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          width="100%" 
          py={2}
        >
          <img 
            src={logoImage} 
            alt="Logo Divertilandia" 
            style={{ 
              width: '120px', 
              height: '120px', 
              objectFit: 'contain' 
            }} 
          />
        </Box>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Divertilandia
          </Typography>
          
          {/* Botón para instalar PWA - visible en móvil y desktop */}
          <Box sx={{ mr: 1 }}>
            <InstallPWA variant="button" showOnDesktop={true} />
          </Box>
          
          <IconButton
            color="inherit"
            onClick={handleLogout}
            title="Cerrar sesión"
            sx={{ 
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
